import { createTheme, responsiveFontSizes } from "@mui/material";
import { APP_UI_FONT_STACK } from './shared/styles/fontStacks';

// colors
const primary = "#000000";
const secondary = "#526572";
const black = "#343a40";
const Black = "#000000"
const darkBlack = "rgb(36, 40, 44)";
const background = "#f5f5f5";
const warningLight = "rgba(253, 200, 69, .3)";
const warningMain = "rgba(253, 200, 69, .5)";
const warningDark = "rgba(253, 200, 69, .7)";
const white = "#f5f5f5"
const Orange = '#E26D5C'
// border
const borderWidth = 2;
const borderColor = "rgba(0, 0, 0, 0.13)";

// breakpoints
const xl = 1920;
const lg = 1280;
const md = 960;
const sm = 600;
const xs = 0;

// spacing
const spacing = 8;

const theme = createTheme({
  palette: {
    primary: { main: primary },
    secondary: { main: secondary },
    Orange:{main:Orange},
    common: {
      black,
      darkBlack
    },
    warning: {
      light: warningLight,
      main: warningMain,
      dark: warningDark
    },
    white: {main: white},
    black: {main: Black},
    // Used to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
    background: {
      default: background
    },
    spacing
  },
  breakpoints: {
    // Define custom breakpoint values.
    // These will apply to Material-UI components that use responsive
    // breakpoints, such as `Grid` and `Hidden`. You can also use the
    // theme breakpoint functions `up`, `down`, and `between` to create
    // media queries for these breakpoints
    values: {
      xl,
      lg,
      md,
      sm,
      xs
    }
  },
  border: {
    borderColor: borderColor,
    borderWidth: borderWidth
  },
  overrides: {
    MuiExpansionPanel: {
      root: {
        position: "static"
      }
    },
    MuiTableCell: {
      root: {
        paddingLeft: spacing * 2,
        paddingRight: spacing * 2,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        }
      }
    },
    MuiDivider: {
      root: {
        backgroundColor: borderColor,
        height: borderWidth
      }
    },
    MuiPrivateNotchedOutline: {
      root: {
        borderWidth: borderWidth
      }
    },
    MuiListItem: {
      divider: {
        borderBottom: `${borderWidth}px solid ${borderColor}`
      }
    },
    MuiDialog: {
      paper: {
        width: "100%",
        maxWidth: 430,
        marginLeft: spacing,
        marginRight: spacing
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: darkBlack
      }
    },
    MuiExpansionPanelDetails: {
      root: {
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        }
      }
    }
  },
  typography: {
    // One familiar UI family across navigation, forms, and headings. Visual
    // hierarchy comes from size and weight rather than switching typefaces.
    fontFamily: APP_UI_FONT_STACK,
    useNextVariants: true,
    htmlFontSize: 16,
    h1: {
      fontFamily: APP_UI_FONT_STACK,
      fontWeight: 400,
      fontSize: '3.2rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h2: {
      fontFamily: APP_UI_FONT_STACK,
      fontWeight: 400,
      fontSize: '2.6rem',
      letterSpacing: '-0.015em',
      lineHeight: 1.2
    },
    h3: {
      fontFamily: APP_UI_FONT_STACK,
      fontWeight: 400,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.25
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.6rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.005em',
      lineHeight: 1.35
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.05rem',
      letterSpacing: '0',
      lineHeight: 1.4
    },
    subtitle1: {
      fontWeight: 500,
      letterSpacing: '0.005em'
    },
    subtitle2: {
      fontWeight: 600,
      letterSpacing: '0.005em'
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.65,
      letterSpacing: '0.005em'
    },
    body2: {
      fontSize: '0.92rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em'
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em'
    },
    overline: {
      letterSpacing: '0.08em',
      fontWeight: 600,
      textTransform: 'uppercase'
    },
    caption: {
      fontSize: '0.8rem',
      lineHeight: 1.4,
      letterSpacing: '0.02em'
    }
  },
  components: {
    // The warning palette above is translucent yellow by design (it tints
    // buttons and chips), but MUI derives a standard Alert's text and icon
    // colours from it, which produced 30%-opacity brown text on a 30%-opacity
    // background: warning notices were barely legible everywhere. Pin the
    // standard warning alert to MUI's own opaque light-mode colours instead.
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          color: '#663c00',
          backgroundColor: '#fff4e5',
          '& .MuiAlert-icon': { color: '#ed6c02' },
        },
      },
    },
  },
});

export default responsiveFontSizes(theme);
